import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import React from 'react';

import {
  createReport,
  IReport,
  querySSPReports,
  ReportActive,
  ReportInactive,
  updateReport,
} from '~/src/dash/api/console-api';
import { updateModel } from '~/src/dash/components/model';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModelSwitchView } from '~/src/dash/components/model-switch-view';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  RouteSSPReportExisting,
  RouteSSPReportNew,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { alignRight, grayColor } from '~/src/dash/utils/material-dashboard-react';
import { routeName } from '~/src/dash/utils/routes';
import { ReportDeleteDialog } from '~/src/dash/views/report-delete-dialog-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  loadingReports: boolean;
  reports: IReport[];

  report?: IReport;

  snackbarMessage: string;
  snackbarError: boolean;
}

export const SSPReportListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      alignRight,
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },

      createReportCont: {
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'flex-end',
      },
      reportName: {
        fontSize: '1rem',
      },
      smallText: {
        color: grayColor[3],
      },
      reportButton: {
        margin: '1rem 0.5rem',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      loadingReports: true,

      reports: [],

      report: undefined,

      snackbarMessage: '',
      snackbarError: false,
    };

    componentDidMount() {
      this.queryReports();
    }

    queryReports = () => {
      this.setState({
        loadingReports: true,
      });
      querySSPReports({
        query: {
          publisher_id: this.props.user.publisher.ID,
        },
      })
        .then((reports: IReport[]) => {
          this.setState({
            loadingReports: false,
            reports,
          });
        })
        .catch(() => {
          this.setState({
            loadingReports: false,
          });
        });
    };

    findReport = (reportID: string): IReport | undefined => {
      return this.state.reports.reduce<IReport | undefined>(
        (selected: IReport | undefined, report: IReport) => {
          return selected || (report.ID === reportID ? report : undefined);
        },
        undefined,
      );
    };

    viewReport = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      const report = this.findReport(currentTarget.value);
      if (report) {
        navigate(
          RouteSSPReportExisting,
          extractRouteParams({
            session: this.props.session,
            user: this.props.user,
          }),
          {
            reportID: report.ID,
          },
        );
      } else {
        this.setState({
          snackbarMessage: `Failed to view report`,
          snackbarError: true,
        });
      }
    };

    deleteReport = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      this.setState({
        report: this.findReport(currentTarget.value),
      });
    };

    handleCloseDeleteDialog = () => {
      this.setState({
        report: undefined,
      });
    };

    handleActiveChange = (report: IReport, checked: boolean) => {
      report.Active = checked ? ReportActive : ReportInactive;
      this.updateReport(report);
    };

    updateReport = (report: IReport) => {
      this.setState({
        reports: updateModel(this.state.reports, report),
      });
      updateReport({
        query: {
          report,
        },
      }).then(
        (report: IReport) => {
          this.setState({
            reports: updateModel(this.state.reports, report),
          });
        },
        () => {
          this.setState({
            snackbarMessage: `Failed to update header bidder [${report.ID}] ${report.Name}`,
            snackbarError: true,
          });
        },
      );
    };

    createReport = () => {
      navigate(
        RouteSSPReportNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    cloneReport = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      const report = this.findReport(currentTarget.value);
      if (report) {
        createReport({
          query: {
            publisher_id: this.props.user.publisher.ID,
            report: Object.assign(report, {
              ID: undefined,
              CreatedAt: undefined,
              UpdatedAt: undefined,
              DeletedAt: undefined,
            }),
          },
        }).then(
          () => {
            this.queryReports();
          },
          () => {
            this.setState({
              snackbarMessage: `Failed to clone report [${report.ID}] ${report.Name}`,
              snackbarError: true,
            });
          },
        );
      } else {
        this.setState({
          snackbarMessage: `Failed to clone report`,
          snackbarError: true,
        });
      }
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    renderReports() {
      const { classes } = this.props;
      if (this.state.loadingReports) {
        return (
          <TableRow>
            <TableCell colSpan={3}>Loading...</TableCell>
          </TableRow>
        );
      }
      if (this.state.reports.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={3}>No reports</TableCell>
          </TableRow>
        );
      }
      return this.state.reports.map((row: IReport) => {
        return (
          <TableRow key={'report_' + row.ID}>
            <TableCell>
              <p className={classes.reportName}>
                <strong>{`[${row.ID}]  ${row.Name}`}</strong>
                <br />
                {row.Subject}
              </p>
              <p className={classes.reportName}></p>
              <p className={classes.smallText}>
                <strong>Created:</strong> {formatDate(row.CreatedAt)}
                <br />
                <strong>Updated:</strong> {formatDate(row.UpdatedAt)}
              </p>
            </TableCell>
            <TableCell>{row.BidderCodes}</TableCell>
            <TableCell align='right'>
              <ModelSwitchView
                checked={row.Active === ReportActive}
                model={row}
                onChange={this.handleActiveChange}
              />
              <ModelSecondaryButton color='primary' value={row.ID} onClick={this.viewReport}>
                View
              </ModelSecondaryButton>
              <Button
                classes={{ root: classes.reportButton }}
                value={row.ID}
                onClick={this.deleteReport}
              >
                Delete
              </Button>
              <Button
                classes={{ root: classes.reportButton }}
                value={row.ID}
                onClick={this.cloneReport}
              >
                Clone
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <Grid container>
                <Grid item xs={12} md={6}></Grid>
                <Grid item xs={12} md={6} className={classes.createReportCont}>
                  <ModelMediumButton
                    variant='contained'
                    color='primary'
                    onClick={this.createReport}
                  >
                    Create new Report
                  </ModelMediumButton>
                </Grid>
              </Grid>
              <Table
                classes={{
                  root: classes.table,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>SSP Name</TableCell>
                    <TableCell>{/* empty */}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{this.renderReports()}</TableBody>
              </Table>
            </Paper>
            <ReportDeleteDialog
              report={this.state.report}
              onError={this.handleCloseDeleteDialog}
              onClose={this.handleCloseDeleteDialog}
              onAccept={this.queryReports}
            />
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
